import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import PublicLayout from '../components/public/PublicLayout';
import SEO from '../components/common/SEO';
import { seoConfig } from '../config/seoConfig';
import axios from 'axios';
import countries from '../arca_Mem/countries';
import '../survey.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const STORAGE_KEY = 'arca2027_survey_draft';

// ARCA Color Scheme
const ARCA = {
    primary: '#6B8E23',
    primaryDark: '#58761C',
    primaryLight: '#8AAF3A',
    primaryBg: 'rgba(107,142,35,0.06)',
    primaryBgHover: 'rgba(107,142,35,0.12)',
    primaryBorder: 'rgba(107,142,35,0.3)',
    selectedBg: 'rgba(107,142,35,0.08)',
    borderGray: '#D1D5DB',
    white: '#ffffff',
    gray50: '#f8f9fa',
    gray100: '#e9ecef',
    gray200: '#dee2e6',
    gray600: '#6c757d',
    dark: '#212529',
    textMuted: '#6B7280',
    textDark: '#374151',
};

const stepVariants = {
    initial: { opacity: 0, x: 40 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.35, ease: 'easeOut' } },
    exit: { opacity: 0, x: -40, transition: { duration: 0.25, ease: 'easeIn' } }
};

const questionVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.3, ease: 'easeOut' } })
};

// Use the shared full country list from membership registration
const COUNTRIES = countries.map(c => c.name);

const PROFESSIONS = [
    'Respiratory Therapist', 'Physician', 'Nurse', 'Physiotherapist', 'Biomedical Scientist',
    'Respiratory Therapy Student', 'Medical Student', 'Lecturer/Faculty', 'Researcher',
    'Hospital Administrator', 'Ministry of Health', 'Ministry of Education', 'Healthcare NGO',
    'Healthcare Industry', 'Medical Equipment Company', 'Policymaker', 'Other'
];

const STUDENT_PROFESSIONAL_OPTIONS = ['Student', 'Professional'];
const EXPERIENCE_OPTIONS = ['Student', 'Less than 2 years', '2-5 years', '6-10 years', '11-20 years', 'More than 20 years'];
const INTEREST_OPTIONS = ['Definitely interested', 'Probably interested', 'Not sure', 'Probably not', 'Not interested'];
const PARTICIPATION_OPTIONS = ['In person', 'Virtual', 'Either', 'Not sure'];
const MOTIVATION_OPTIONS = [
    'International speakers', 'Clinical education', 'Hands-on workshops', 'Networking',
    'Research presentations', 'Career development', 'Student opportunities', 'Innovation showcase',
    'Industry exhibition', 'Leadership development', 'Policy discussions', 'CPD/CME', 'Other'
];
const TOPIC_OPTIONS = [
    'Mechanical Ventilation', 'Critical Care', 'Pediatric Respiratory Care', 'Neonatal Respiratory Care',
    'Pulmonary Function Testing', 'Oxygen Therapy', 'Asthma', 'COPD', 'Tuberculosis', 'Sleep Medicine',
    'Emergency Respiratory Care', 'Artificial Intelligence', 'Respiratory Education', 'Leadership',
    'Research & Innovation', 'Public Health', 'Lung Health in Africa', 'Policy & Advocacy', 'Other'
];
const FORMAT_OPTIONS = [
    'Keynotes', 'Panel discussions', 'Interactive workshops', 'Hands-on demonstrations',
    'Clinical case discussions', 'Research presentations', 'Poster sessions', 'Simulation', 'Roundtables'
];
const RESEARCH_OPTIONS = ['Yes', 'Maybe', 'No'];
const NETWORKING_OPTIONS = [
    'International experts', 'Africa-wide networking', 'Students', 'Mentorship',
    'Career opportunities', 'Research collaborations', 'Industry partnerships', 'Country networking'
];
const EXHIBITOR_OPTIONS = [
    'Medical equipment', 'Ventilators', 'Oxygen systems', 'Pharmaceuticals', 'Universities',
    'NGOs', 'Health technology', 'Publishers', 'Recruitment', 'Other'
];
const ATTENDANCE_FACTOR_OPTIONS = [
    'Registration fee', 'Travel', 'Accommodation', 'Programme quality', 'Speakers',
    'Hands-on training', 'Networking', 'CPD/CME', 'Visa support'
];
const FEE_OPTIONS = ['Under $50', '$50-100', '$101-200', 'Above $200', 'Depends on benefits'];
const PRACTICE_AREA_OPTIONS = [
    'Adult Critical Care', 'Pediatric Care', 'Neonatal Care', 'Pulmonary Diagnostics',
    'Emergency Care', 'Education & Academia', 'Research', 'Public Health',
    'Healthcare Administration', 'Industry', 'Student', 'Other'
];
const HEARD_ABOUT_OPTIONS = [
    'ARCA Website', 'LinkedIn', 'Facebook', 'Instagram', 'X', 'WhatsApp', 'Google',
    'University', 'Hospital', 'Friend/Colleague', 'Conference/Event', 'Other'
];
const VOLUNTEER_OPTIONS = ['Yes', 'Maybe', 'No'];
const UPDATES_OPTIONS = ['Yes', 'No'];

const initialFormData = {
    profession: '', country: '', organization: '', studentProfessional: '', yearsOfExperience: '',
    interestAttending: '', participationMode: '', topMotivations: [],
    preferredTopics: [], preferredFormats: [],
    submitResearch: '', encouragePresent: '',
    preferredNetworking: [],
    preferredExhibitors: [],
    topAttendanceFactors: [], reasonableFee: '',
    biggestChallenge: '', arcaPriorities: '', speakerInvite: '', uniqueFeature: '', additionalComments: '',
    primaryPracticeArea: '',
    heardAboutArca: [], interestedVolunteering: '', receiveUpdates: '', email: '',
    otherFields: {}
};

// Checkbox component for multi-select — Premium card-style options with "Other" text support
const CheckboxGroup = ({ options, selected, onChange, columns = 2, otherText = '', onOtherTextChange }) => {
    const handleToggle = (option) => {
        if (selected.includes(option)) {
            onChange(selected.filter(s => s !== option));
        } else {
            onChange([...selected, option]);
        }
    };
    const hasOther = options.includes('Other');
    const isOtherSelected = selected.includes('Other');

    return (
        <div>
            <div className="row g-3">
                {options.map(option => {
                    const isSelected = selected.includes(option);
                    return (
                        <div key={option} className={`col-md-${12 / columns}`}>
                            <motion.div
                                className="arca-option-card form-check d-flex align-items-center"
                                whileHover={{ y: -2, boxShadow: `0 4px 16px rgba(0,0,0,0.06), 0 0 0 1.5px ${ARCA.primary}` }}
                                whileTap={{ scale: 0.98 }}
                                style={{
                                    cursor: 'pointer',
                                    padding: '14px 18px',
                                    borderRadius: 12,
                                    border: `2px solid ${isSelected ? ARCA.primary : ARCA.borderGray}`,
                                    backgroundColor: isSelected ? ARCA.selectedBg : ARCA.white,
                                    transition: 'border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease',
                                    boxShadow: isSelected ? `0 2px 8px ${ARCA.primaryBorder}` : 'none',
                                    gap: 12,
                                }}
                                onClick={() => handleToggle(option)}
                            >
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleToggle(option)}
                                    style={{ cursor: 'pointer' }}
                                />
                                <label className="form-check-label mb-0" style={{
                                    cursor: 'pointer',
                                    color: isSelected ? ARCA.primaryDark : ARCA.textDark,
                                    fontWeight: isSelected ? 600 : 500,
                                    fontSize: '0.9rem',
                                    lineHeight: 1.5,
                                }}>{option}</label>
                            </motion.div>
                        </div>
                    );
                })}
            </div>
            {hasOther && isOtherSelected && onOtherTextChange && (
                <motion.input
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    type="text"
                    className="form-control mt-2"
                    placeholder="Please specify..."
                    value={otherText}
                    onChange={(e) => onOtherTextChange(e.target.value)}
                    style={{ borderColor: ARCA.gray200, borderRadius: 10 }}
                />
            )}
        </div>
    );
};

// Option button for single-select — Premium card-style options
const OptionButton = ({ option, selected, onClick }) => (
    <motion.button
        type="button"
        whileHover={{ y: -2, boxShadow: `0 4px 16px rgba(0,0,0,0.06), 0 0 0 1.5px ${ARCA.primary}` }}
        whileTap={{ scale: 0.97 }}
        onClick={onClick}
        style={{
            padding: '12px 22px',
            borderRadius: 12,
            border: `2px solid ${selected ? ARCA.primary : ARCA.borderGray}`,
            backgroundColor: selected ? ARCA.selectedBg : ARCA.white,
            color: selected ? ARCA.primaryDark : ARCA.textDark,
            fontSize: '0.9rem',
            fontWeight: selected ? 600 : 500,
            lineHeight: 1.4,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: selected ? `0 2px 8px ${ARCA.primaryBorder}` : 'none',
        }}
    >
        {option}
    </motion.button>
);

const Arca2027SurveyPage = () => {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState(initialFormData);
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [showReview, setShowReview] = useState(false);

    // Load draft from localStorage on mount
    useEffect(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed.formData) setFormData(parsed.formData);
                if (parsed.step) setStep(parsed.step);
                if (parsed.submitted) { setSubmitted(true); }
            }
        } catch (e) { /* ignore parse errors */ }
    }, []);

    // Auto-save draft to localStorage
    useEffect(() => {
        if (!submitted) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ formData, step, submitted }));
        }
    }, [formData, step, submitted]);

    const handleChange = (field) => (e) => {
        setFormData(prev => ({ ...prev, [field]: e.target.value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleMultiSelect = (field) => (values) => {
        setFormData(prev => ({ ...prev, [field]: values }));
    };

    const handleOtherText = (field) => (value) => {
        setFormData(prev => ({
            ...prev,
            otherFields: { ...prev.otherFields, [field]: value }
        }));
    };

    // Validate current step
    const validateStep = useCallback((currentStep) => {
        const newErrors = {};

        if (currentStep === 1) {
            if (!formData.profession) newErrors.profession = 'Please select your profession';
            if (!formData.country) newErrors.country = 'Please select your country';
        }

        // Step 2 and 3 are mostly optional
        if (currentStep === 3) {
            if (!formData.heardAboutArca || formData.heardAboutArca.length === 0) {
                newErrors.heardAboutArca = 'Please select at least one option';
            }
            if (formData.receiveUpdates === 'Yes' && !formData.email) {
                newErrors.email = 'Please provide your email to receive updates';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData]);

    const nextStep = () => {
        if (validateStep(step)) {
            setStep(prev => Math.min(prev + 1, 3));
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const prevStep = () => {
        setStep(prev => Math.max(prev - 1, 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async () => {
        if (!validateStep(3)) return;

        try {
            setSubmitting(true);
            // Process "Other" fields: replace plain "Other" with "Other: <custom text>"
            const submitData = { ...formData };
            const otherFields = formData.otherFields || {};
            // Multi-select arrays
            ['topMotivations', 'preferredTopics', 'preferredExhibitors', 'heardAboutArca'].forEach(field => {
                if (Array.isArray(submitData[field]) && submitData[field].includes('Other') && otherFields[field]) {
                    submitData[field] = submitData[field].map(v => v === 'Other' ? `Other: ${otherFields[field]}` : v);
                }
            });
            // Single-select dropdowns
            if (submitData.profession === 'Other' && otherFields.profession) {
                submitData.profession = `Other: ${otherFields.profession}`;
            }
            if (submitData.primaryPracticeArea === 'Other' && otherFields.primaryPracticeArea) {
                submitData.primaryPracticeArea = `Other: ${otherFields.primaryPracticeArea}`;
            }
            // Remove otherFields before submitting
            delete submitData.otherFields;

            const response = await axios.post(`${API_URL}/survey/submit`, submitData);

            if (response.data.success) {
                toast.success('Thank you for completing the ARCA 2027 survey!', { autoClose: 5000 });
                setSubmitted(true);
                localStorage.removeItem(STORAGE_KEY);
            }
        } catch (error) {
            console.error('Survey submission error:', error);
            toast.error(error.response?.data?.message || 'Failed to submit survey. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const progressPercent = submitted ? 100 : Math.round((step / 3) * 100);

    // Thank You Page
    if (submitted) {
        return (
            <PublicLayout>
                <SEO
                    title={seoConfig.arca2027Survey.title}
                    description={seoConfig.arca2027Survey.description}
                    keywords={seoConfig.arca2027Survey.keywords}
                    image="/arca.survey.logo.jpg"
                    url={seoConfig.arca2027Survey.url}
                    structuredData={{
                        '@context': 'https://schema.org',
                        '@type': 'Event',
                        'name': 'ARCA 2027 International Conference',
                        'description': 'ARCA\'s first-ever International Conference in Accra, Ghana',
                        'startDate': '2027-10-07',
                        'endDate': '2027-10-09',
                        'eventStatus': 'https://schema.org/EventScheduled',
                        'eventAttendanceMode': 'https://schema.org/OfflineEventAttendanceMode',
                        'location': {
                            '@type': 'Place',
                            'name': 'Accra, Ghana',
                            'address': { '@type': 'PostalAddress', 'addressLocality': 'Accra', 'addressCountry': 'GH' }
                        },
                        'organizer': {
                            '@type': 'Organization',
                            'name': 'Africa Respiratory Care Association',
                            'url': 'https://www.africaresp.org'
                        }
                    }}
                />
                <div className="py-5" style={{ backgroundColor: ARCA.white, minHeight: '70vh' }}>
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-8 text-center">
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    transition={{ duration: 0.5, ease: 'easeOut' }}
                                    className="card shadow-sm"
                                    style={{ border: `2px solid ${ARCA.primaryBorder}`, borderRadius: 16, overflow: 'hidden' }}
                                >
                                    <div className="card-body p-5">
                                        <motion.div
                                            className="mb-4"
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.2 }}
                                        >
                                            <div style={{ width: 80, height: 80, borderRadius: '50%', background: `linear-gradient(135deg, ${ARCA.primary} 0%, ${ARCA.primaryLight} 100%)`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 8px 24px ${ARCA.primaryBorder}` }}>
                                                <i className="bi bi-check-lg text-white" style={{ fontSize: 40 }}></i>
                                            </div>
                                        </motion.div>
                                        <motion.h1 className="h2 fw-bold mb-3" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} style={{ color: ARCA.dark }}>Thank You!</motion.h1>
                                        <motion.p className="lead text-muted mb-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}>
                                            Your feedback will directly help shape ARCA's first-ever International Conference
                                            in Accra, Ghana (October 7-9, 2027). We truly appreciate your time and input.
                                        </motion.p>
                                        <hr className="my-4" />
                                        <motion.div className="row g-3 mt-4" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}>
                                            <div className="col-md-6">
                                                <Link to="/" className="btn w-100 py-3" style={{ borderColor: ARCA.primary, color: ARCA.primary, borderWidth: 1.5 }}>
                                                    <i className="bi bi-house me-2"></i>Visit Homepage
                                                </Link>
                                            </div>
                                            <div className="col-md-6">
                                                <Link to="/events" className="btn w-100 py-3" style={{ borderColor: ARCA.primary, color: ARCA.primary, borderWidth: 1.5 }}>
                                                    <i className="bi bi-calendar-event me-2"></i>Learn About ARCA 2027
                                                </Link>
                                            </div>
                                            <div className="col-md-6">
                                                <Link to="/membership_registration" className="btn w-100 py-3" style={{ backgroundColor: ARCA.primary, borderColor: ARCA.primary, color: ARCA.white, borderWidth: 1.5 }}>
                                                    <i className="bi bi-person-plus me-2"></i>Become an ARCA Member
                                                </Link>
                                            </div>
                                            <div className="col-md-6">
                                                <button
                                                    className="btn w-100 py-3"
                                                    style={{ borderColor: ARCA.gray200, color: ARCA.gray600, borderWidth: 1.5 }}
                                                    onClick={() => {
                                                        const url = window.location.href;
                                                        if (navigator.share) {
                                                            navigator.share({ title: 'ARCA 2027 Survey', text: 'Help shape ARCA\'s first International Conference!', url });
                                                        } else {
                                                            navigator.clipboard.writeText(url);
                                                            toast.success('Link copied to clipboard!');
                                                        }
                                                    }}
                                                >
                                                    <i className="bi bi-share me-2"></i>Share This Survey
                                                </button>
                                            </div>
                                        </motion.div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </div>
            </PublicLayout>
        );
    }

    return (
        <PublicLayout>
            <SEO
                title={seoConfig.arca2027Survey.title}
                description={seoConfig.arca2027Survey.description}
                keywords={seoConfig.arca2027Survey.keywords}
                image="/arca.survey.logo.jpg"
                url={seoConfig.arca2027Survey.url}
                structuredData={{
                    '@context': 'https://schema.org',
                    '@type': 'Event',
                    'name': 'ARCA 2027 International Conference',
                    'description': 'ARCA\'s first-ever International Conference in Accra, Ghana',
                    'startDate': '2027-10-07',
                    'endDate': '2027-10-09',
                    'eventStatus': 'https://schema.org/EventScheduled',
                    'eventAttendanceMode': 'https://schema.org/OfflineEventAttendanceMode',
                    'location': {
                        '@type': 'Place',
                        'name': 'Accra, Ghana',
                        'address': { '@type': 'PostalAddress', 'addressLocality': 'Accra', 'addressCountry': 'GH' }
                    },
                    'organizer': {
                        '@type': 'Organization',
                        'name': 'Africa Respiratory Care Association',
                        'url': 'https://www.africaresp.org'
                    }
                }}
            />
            <div className="py-4 py-md-5" style={{ backgroundColor: ARCA.white, minHeight: '80vh' }}>
                <div className="container">
                    {/* Header / Banner */}
                    <div className="row justify-content-center mb-4">
                        <div className="col-lg-10 text-center">
                            <motion.div
                                initial={{ opacity: 0, y: -15 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.45 }}
                                className="card"
                                style={{ background: `linear-gradient(135deg, ${ARCA.primary} 0%, ${ARCA.primaryDark} 50%, #4A6315 100%)`, borderRadius: 16, overflow: 'hidden', boxShadow: `0 8px 32px ${ARCA.primaryBorder}` }}
                            >
                                <div className="card-body p-4 text-white">
                                    <h1 className="h3 fw-bold mb-2">Help Shape ARCA's First International Conference (ARCA 2027)</h1>
                                    <p className="mb-0" style={{ opacity: 0.9 }}>
                                        The Africa Respiratory Care Association (ARCA) is preparing to host its first-ever International Conference
                                        in Accra, Ghana (October 7-9, 2027). Your feedback will directly influence the planning of this historic event.
                                        This survey takes approximately 2-3 minutes.
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="row justify-content-center mb-4">
                        <div className="col-lg-10">
                            <div className="d-flex justify-content-between mb-2">
                                <span className="small fw-semibold" style={{ color: ARCA.primary }}>Step {step} of 3</span>
                                <span className="small fw-semibold" style={{ color: ARCA.gray600 }}>{progressPercent}% Complete</span>
                            </div>
                            <div className="progress" style={{ height: 10, borderRadius: 10, backgroundColor: ARCA.gray100 }}>
                                <motion.div
                                    className="progress-bar"
                                    role="progressbar"
                                    animate={{ width: `${progressPercent}%` }}
                                    transition={{ duration: 0.5, ease: 'easeOut' }}
                                    style={{ backgroundColor: ARCA.primary, borderRadius: 10 }}
                                />
                            </div>
                            <div className="d-flex justify-content-between mt-3">
                                {['About You & Interest', 'Conference Preferences', 'Future & Stay Connected'].map((label, i) => {
                                    const isActive = step === i + 1;
                                    const isDone = step > i + 1;
                                    return (
                                        <motion.span
                                            key={i}
                                            className="small d-flex align-items-center gap-1"
                                            initial={{ opacity: 0, y: 5 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: i * 0.1 + 0.2 }}
                                            style={{
                                                fontWeight: isActive ? 700 : isDone ? 600 : 400,
                                                color: isDone ? ARCA.primary : isActive ? ARCA.dark : ARCA.gray600,
                                            }}
                                        >
                                            {isDone && <span style={{ color: ARCA.primary, fontSize: 16 }}>✓</span>}
                                            {isActive && <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: ARCA.primary, display: 'inline-block' }} />}
                                            <span className="d-none d-sm-inline ms-1">{label}</span>
                                            <span className="d-inline d-sm-none">{`S${i + 1}`}</span>
                                        </motion.span>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Form Card */}
                    <div className="row justify-content-center">
                        <div className="col-lg-10">
                            <div className="card arca-survey-card" style={{ border: `2px solid ${ARCA.gray200}`, borderRadius: 16, backgroundColor: ARCA.white, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
                                <div className="card-body p-4 p-md-5">
                                    <AnimatePresence mode="wait">
                                        {/* ===== STEP 1: About You & Interest ===== */}
                                        {step === 1 && (
                                            <motion.div key="step1" variants={stepVariants} initial="initial" animate="animate" exit="exit">
                                                <h2 className="h4 fw-bold mb-4" style={{ color: ARCA.dark }}>
                                                    <i className="bi bi-person-circle me-2" style={{ color: ARCA.primary }}></i>
                                                    About You & Your Interest
                                                </h2>

                                                {/* Q1: Profession */}
                                                <motion.div className="mb-4" custom={0} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">1. What is your profession? <span className="text-danger">*</span></label>
                                                    <select className={`form-select ${errors.profession ? 'is-invalid' : ''}`} value={formData.profession} onChange={handleChange('profession')} style={{ borderColor: errors.profession ? undefined : ARCA.gray200 }}>
                                                        <option value="">-- Select your profession --</option>
                                                        {PROFESSIONS.map(p => <option key={p} value={p}>{p}</option>)}
                                                    </select>
                                                    {errors.profession && <div className="invalid-feedback">{errors.profession}</div>}
                                                    {formData.profession === 'Other' && (
                                                        <motion.input initial={{ opacity: 0 }} animate={{ opacity: 1 }} type="text" className="form-control mt-2" placeholder="Please specify your profession..." value={formData.otherFields?.profession || ''} onChange={(e) => handleOtherText('profession')(e.target.value)} style={{ borderColor: ARCA.gray200, borderRadius: 10 }} />
                                                    )}
                                                </motion.div>

                                                {/* Q2: Country */}
                                                <motion.div className="mb-4" custom={1} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">2. Which country are you from? <span className="text-danger">*</span></label>
                                                    <select className={`form-select ${errors.country ? 'is-invalid' : ''}`} value={formData.country} onChange={handleChange('country')} style={{ borderColor: errors.country ? undefined : ARCA.gray200 }}>
                                                        <option value="">-- Select your country --</option>
                                                        {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                                                    </select>
                                                    {errors.country && <div className="invalid-feedback">{errors.country}</div>}
                                                </motion.div>

                                                {/* Q2a: Organization */}
                                                <motion.div className="mb-4" custom={2} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">3. Organization / Institution (optional)</label>
                                                    <input type="text" className="form-control" value={formData.organization} onChange={handleChange('organization')} placeholder="e.g. University of Ghana, Korle Bu Teaching Hospital, ARCA..." style={{ borderColor: ARCA.gray200, borderRadius: 10 }} />
                                                </motion.div>

                                                {/* Q2b: Student or Professional */}
                                                <motion.div className="mb-4" custom={3} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">4. Are you currently a Student or a Professional? (optional)</label>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {STUDENT_PROFESSIONAL_OPTIONS.map(option => (
                                                            <OptionButton key={option} option={option} selected={formData.studentProfessional === option} onClick={() => setFormData(prev => ({ ...prev, studentProfessional: option }))} />
                                                        ))}
                                                    </div>
                                                </motion.div>

                                                {/* Q3: Years of Experience */}
                                                <motion.div className="mb-4" custom={4} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">5. Years of experience</label>
                                                    <select className="form-select" value={formData.yearsOfExperience} onChange={handleChange('yearsOfExperience')} style={{ borderColor: ARCA.gray200 }}>
                                                        <option value="">-- Select --</option>
                                                        {EXPERIENCE_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
                                                    </select>
                                                </motion.div>

                                                {/* Q4: Interest in Attending */}
                                                <motion.div className="mb-4" custom={5} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">6. How interested are you in attending ARCA 2027?</label>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {INTEREST_OPTIONS.map(option => (
                                                            <OptionButton key={option} option={option} selected={formData.interestAttending === option} onClick={() => setFormData(prev => ({ ...prev, interestAttending: option }))} />
                                                        ))}
                                                    </div>
                                                </motion.div>

                                                {/* Q5: Participation Mode */}
                                                <motion.div className="mb-4" custom={6} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">7. How would you prefer to participate?</label>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {PARTICIPATION_OPTIONS.map(option => (
                                                            <OptionButton key={option} option={option} selected={formData.participationMode === option} onClick={() => setFormData(prev => ({ ...prev, participationMode: option }))} />
                                                        ))}
                                                    </div>
                                                </motion.div>

                                                {/* Q6: Top 3 Motivations */}
                                                <motion.div className="mb-4" custom={7} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">8. What are your top motivations for attending? <small className="text-muted">(select up to 3)</small></label>
                                                    <CheckboxGroup
                                                        options={MOTIVATION_OPTIONS}
                                                        selected={formData.topMotivations}
                                                        onChange={(vals) => {
                                                            if (vals.length <= 3) handleMultiSelect('topMotivations')(vals);
                                                            else toast.info('Please select a maximum of 3 motivations.');
                                                        }}
                                                        columns={3}
                                                        otherText={formData.otherFields?.topMotivations || ''}
                                                        onOtherTextChange={handleOtherText('topMotivations')}
                                                    />
                                                </motion.div>
                                            </motion.div>
                                        )}

                                        {/* ===== STEP 2: Conference Preferences ===== */}
                                        {step === 2 && (
                                            <motion.div key="step2" variants={stepVariants} initial="initial" animate="animate" exit="exit">
                                                <h2 className="h4 fw-bold mb-4" style={{ color: ARCA.dark }}>
                                                    <i className="bi bi-calendar-check me-2" style={{ color: ARCA.primary }}></i>
                                                    Conference Preferences
                                                </h2>

                                                {/* Q7: Preferred Topics */}
                                                <motion.div className="mb-4" custom={0} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">7. Which topics interest you most?</label>
                                                    <CheckboxGroup options={TOPIC_OPTIONS} selected={formData.preferredTopics} onChange={handleMultiSelect('preferredTopics')} columns={3} otherText={formData.otherFields?.preferredTopics || ''} onOtherTextChange={handleOtherText('preferredTopics')} />
                                                </motion.div>

                                                {/* Q8: Preferred Session Formats */}
                                                <motion.div className="mb-4" custom={1} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">8. Preferred session formats</label>
                                                    <CheckboxGroup options={FORMAT_OPTIONS} selected={formData.preferredFormats} onChange={handleMultiSelect('preferredFormats')} columns={3} />
                                                </motion.div>

                                                {/* Q9: Submit Research */}
                                                <motion.div className="mb-4" custom={2} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">9. Would you like to submit research for presentation?</label>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {RESEARCH_OPTIONS.map(option => (
                                                            <OptionButton key={option} option={option} selected={formData.submitResearch === option} onClick={() => setFormData(prev => ({ ...prev, submitResearch: option }))} />
                                                        ))}
                                                    </div>
                                                </motion.div>

                                                {/* Q10: Encourage to Present */}
                                                <motion.div className="mb-4" custom={3} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">10. What would encourage you to present at ARCA 2027?</label>
                                                    <textarea className="form-control" rows={3} value={formData.encouragePresent} onChange={handleChange('encouragePresent')} placeholder="Share what would motivate you to present..." style={{ borderColor: ARCA.gray200 }} />
                                                </motion.div>

                                                {/* Q11: Preferred Networking */}
                                                <motion.div className="mb-4" custom={4} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">11. What networking opportunities interest you?</label>
                                                    <CheckboxGroup options={NETWORKING_OPTIONS} selected={formData.preferredNetworking} onChange={handleMultiSelect('preferredNetworking')} columns={2} />
                                                </motion.div>

                                                {/* Q12: Preferred Exhibitors */}
                                                <motion.div className="mb-4" custom={5} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">12. What types of exhibitors would you like to see?</label>
                                                    <CheckboxGroup options={EXHIBITOR_OPTIONS} selected={formData.preferredExhibitors} onChange={handleMultiSelect('preferredExhibitors')} columns={2} otherText={formData.otherFields?.preferredExhibitors || ''} onOtherTextChange={handleOtherText('preferredExhibitors')} />
                                                </motion.div>

                                                {/* Q13: Top 3 Attendance Factors */}
                                                <motion.div className="mb-4" custom={6} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">13. Top factors that would affect your attendance <small className="text-muted">(select up to 3)</small></label>
                                                    <CheckboxGroup
                                                        options={ATTENDANCE_FACTOR_OPTIONS}
                                                        selected={formData.topAttendanceFactors}
                                                        onChange={(vals) => {
                                                            if (vals.length <= 3) handleMultiSelect('topAttendanceFactors')(vals);
                                                            else toast.info('Please select a maximum of 3 factors.');
                                                        }}
                                                        columns={3}
                                                    />
                                                </motion.div>

                                                {/* Q14: Reasonable Fee */}
                                                <motion.div className="mb-4" custom={7} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">14. What would be a reasonable registration fee?</label>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {FEE_OPTIONS.map(option => (
                                                            <OptionButton key={option} option={option} selected={formData.reasonableFee === option} onClick={() => setFormData(prev => ({ ...prev, reasonableFee: option }))} />
                                                        ))}
                                                    </div>
                                                </motion.div>
                                            </motion.div>
                                        )}

                                        {/* ===== STEP 3: Future & Stay Connected ===== */}
                                        {step === 3 && (
                                            <motion.div key="step3" variants={stepVariants} initial="initial" animate="animate" exit="exit">
                                                <h2 className="h4 fw-bold mb-4" style={{ color: ARCA.dark }}>
                                                    <i className="bi bi-stars me-2" style={{ color: ARCA.primary }}></i>
                                                    Future Vision & Stay Connected
                                                </h2>

                                                {/* Q15: Biggest Challenge */}
                                                <motion.div className="mb-4" custom={0} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">15. What is the biggest respiratory care challenge in Africa?</label>
                                                    <textarea className="form-control" rows={3} value={formData.biggestChallenge} onChange={handleChange('biggestChallenge')} placeholder="Share your thoughts..." style={{ borderColor: ARCA.gray200 }} />
                                                </motion.div>

                                                {/* Q16: ARCA Priorities */}
                                                <motion.div className="mb-4" custom={1} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">16. What should ARCA prioritize over the next five years?</label>
                                                    <textarea className="form-control" rows={3} value={formData.arcaPriorities} onChange={handleChange('arcaPriorities')} placeholder="Share your ideas..." style={{ borderColor: ARCA.gray200 }} />
                                                </motion.div>

                                                {/* Q17: Speaker to Invite */}
                                                <motion.div className="mb-4" custom={2} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">17. Name one international speaker you'd like to see at ARCA 2027</label>
                                                    <input type="text" className="form-control" value={formData.speakerInvite} onChange={handleChange('speakerInvite')} placeholder="Speaker name or suggestion" style={{ borderColor: ARCA.gray200 }} />
                                                </motion.div>

                                                {/* Q18: Unique Feature */}
                                                <motion.div className="mb-4" custom={3} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">18. One unique feature ARCA's conference should offer?</label>
                                                    <input type="text" className="form-control" value={formData.uniqueFeature} onChange={handleChange('uniqueFeature')} placeholder="Your suggestion" style={{ borderColor: ARCA.gray200 }} />
                                                </motion.div>

                                                {/* Q19: Additional Comments */}
                                                <motion.div className="mb-4" custom={4} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">19. Any additional comments?</label>
                                                    <textarea className="form-control" rows={3} value={formData.additionalComments} onChange={handleChange('additionalComments')} placeholder="Anything else you'd like to share..." style={{ borderColor: ARCA.gray200 }} />
                                                </motion.div>

                                                {/* Q20: Practice Area */}
                                                <motion.div className="mb-4" custom={5} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">20. Your primary area of practice</label>
                                                    <select className="form-select" value={formData.primaryPracticeArea} onChange={handleChange('primaryPracticeArea')} style={{ borderColor: ARCA.gray200 }}>
                                                        <option value="">-- Select --</option>
                                                        {PRACTICE_AREA_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                                                    </select>
                                                    {formData.primaryPracticeArea === 'Other' && (
                                                        <motion.input initial={{ opacity: 0 }} animate={{ opacity: 1 }} type="text" className="form-control mt-2" placeholder="Please specify your practice area..." value={formData.otherFields?.primaryPracticeArea || ''} onChange={(e) => handleOtherText('primaryPracticeArea')(e.target.value)} style={{ borderColor: ARCA.gray200, borderRadius: 10 }} />
                                                    )}
                                                </motion.div>

                                                {/* Q21: How did you hear about ARCA */}
                                                <motion.div className="mb-4" custom={6} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">21. How did you hear about ARCA? <span className="text-danger">*</span></label>
                                                    <CheckboxGroup options={HEARD_ABOUT_OPTIONS} selected={formData.heardAboutArca} onChange={handleMultiSelect('heardAboutArca')} columns={3} otherText={formData.otherFields?.heardAboutArca || ''} onOtherTextChange={handleOtherText('heardAboutArca')} />
                                                    {errors.heardAboutArca && <div className="invalid-feedback d-block mt-1">{errors.heardAboutArca}</div>}
                                                </motion.div>

                                                {/* Q22: Volunteering */}
                                                <motion.div className="mb-4" custom={7} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">22. Would you be interested in volunteering for ARCA 2027?</label>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {VOLUNTEER_OPTIONS.map(option => (
                                                            <OptionButton key={option} option={option} selected={formData.interestedVolunteering === option} onClick={() => setFormData(prev => ({ ...prev, interestedVolunteering: option }))} />
                                                        ))}
                                                    </div>
                                                </motion.div>

                                                {/* Q23: Receive Updates */}
                                                <motion.div className="mb-4" custom={8} variants={questionVariants} initial="hidden" animate="visible">
                                                    <label className="form-label fw-semibold">23. Would you like to receive ARCA updates?</label>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {UPDATES_OPTIONS.map(option => (
                                                            <OptionButton key={option} option={option} selected={formData.receiveUpdates === option} onClick={() => setFormData(prev => ({ ...prev, receiveUpdates: option }))} />
                                                        ))}
                                                    </div>
                                                </motion.div>

                                                {/* Q24: Email (conditional) */}
                                                {formData.receiveUpdates === 'Yes' && (
                                                    <motion.div className="mb-4" custom={9} variants={questionVariants} initial="hidden" animate="visible">
                                                        <label className="form-label fw-semibold">24. Your email address</label>
                                                        <input
                                                            type="email"
                                                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                                            value={formData.email}
                                                            onChange={handleChange('email')}
                                                            placeholder="Enter your email to receive updates"
                                                            style={{ borderColor: errors.email ? undefined : ARCA.gray200 }}
                                                        />
                                                        {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                                                    </motion.div>
                                                )}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>

                                    {/* ===== REVIEW SUMMARY ===== */}
                                    {showReview && (
                                        <motion.div key="review" variants={stepVariants} initial="initial" animate="animate" exit="exit">
                                            <h2 className="h4 fw-bold mb-4" style={{ color: ARCA.dark }}>
                                                <i className="bi bi-clipboard-check me-2" style={{ color: ARCA.primary }}></i>
                                                Review Your Answers
                                            </h2>
                                            <p className="text-muted small mb-4">Please review your responses before submitting. Click any section to go back and edit.</p>

                                            {/* Step 1 Summary */}
                                            <motion.div className="mb-3 p-3 rounded-3" custom={0} variants={questionVariants} initial="hidden" animate="visible"
                                                style={{ border: `1px solid ${ARCA.gray200}`, backgroundColor: ARCA.gray50, cursor: 'pointer' }}
                                                onClick={() => { setShowReview(false); setStep(1); }}>
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <h6 className="fw-bold mb-0" style={{ color: ARCA.primary }}>Step 1: About You & Your Interest</h6>
                                                    <span className="text-muted small"><i className="bi bi-pencil-square"></i> Edit</span>
                                                </div>
                                                <div className="row g-2 small">
                                                    <div className="col-sm-6"><strong>Profession:</strong> {formData.profession || <span className="text-muted">—</span>}</div>
                                                    <div className="col-sm-6"><strong>Country:</strong> {formData.country || <span className="text-muted">—</span>}</div>
                                                    <div className="col-sm-6"><strong>Experience:</strong> {formData.yearsOfExperience || <span className="text-muted">—</span>}</div>
                                                    <div className="col-sm-6"><strong>Interest Level:</strong> {formData.interestAttending || <span className="text-muted">—</span>}</div>
                                                    <div className="col-sm-6"><strong>Participation:</strong> {formData.participationMode || <span className="text-muted">—</span>}</div>
                                                    <div className="col-12"><strong>Top Motivations:</strong> {formData.topMotivations.length ? formData.topMotivations.join(', ') : <span className="text-muted">—</span>}</div>
                                                </div>
                                            </motion.div>

                                            {/* Step 2 Summary */}
                                            <motion.div className="mb-3 p-3 rounded-3" custom={1} variants={questionVariants} initial="hidden" animate="visible"
                                                style={{ border: `1px solid ${ARCA.gray200}`, backgroundColor: ARCA.gray50, cursor: 'pointer' }}
                                                onClick={() => { setShowReview(false); setStep(2); }}>
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <h6 className="fw-bold mb-0" style={{ color: ARCA.primary }}>Step 2: Conference Preferences</h6>
                                                    <span className="text-muted small"><i className="bi bi-pencil-square"></i> Edit</span>
                                                </div>
                                                <div className="row g-2 small">
                                                    <div className="col-12"><strong>Topics:</strong> {formData.preferredTopics.length ? formData.preferredTopics.join(', ') : <span className="text-muted">—</span>}</div>
                                                    <div className="col-12"><strong>Formats:</strong> {formData.preferredFormats.length ? formData.preferredFormats.join(', ') : <span className="text-muted">—</span>}</div>
                                                    <div className="col-sm-6"><strong>Submit Research:</strong> {formData.submitResearch || <span className="text-muted">—</span>}</div>
                                                    <div className="col-sm-6"><strong>Reasonable Fee:</strong> {formData.reasonableFee || <span className="text-muted">—</span>}</div>
                                                    <div className="col-12"><strong>Networking:</strong> {formData.preferredNetworking.length ? formData.preferredNetworking.join(', ') : <span className="text-muted">—</span>}</div>
                                                    <div className="col-12"><strong>Exhibitors:</strong> {formData.preferredExhibitors.length ? formData.preferredExhibitors.join(', ') : <span className="text-muted">—</span>}</div>
                                                    <div className="col-12"><strong>Top Attendance Factors:</strong> {formData.topAttendanceFactors.length ? formData.topAttendanceFactors.join(', ') : <span className="text-muted">—</span>}</div>
                                                    {formData.encouragePresent && <div className="col-12"><strong>Encourage to Present:</strong> {formData.encouragePresent}</div>}
                                                </div>
                                            </motion.div>

                                            {/* Step 3 Summary */}
                                            <motion.div className="mb-3 p-3 rounded-3" custom={2} variants={questionVariants} initial="hidden" animate="visible"
                                                style={{ border: `1px solid ${ARCA.gray200}`, backgroundColor: ARCA.gray50, cursor: 'pointer' }}
                                                onClick={() => { setShowReview(false); setStep(3); }}>
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <h6 className="fw-bold mb-0" style={{ color: ARCA.primary }}>Step 3: Future Vision & Stay Connected</h6>
                                                    <span className="text-muted small"><i className="bi bi-pencil-square"></i> Edit</span>
                                                </div>
                                                <div className="row g-2 small">
                                                    {formData.biggestChallenge && <div className="col-12"><strong>Biggest Challenge:</strong> {formData.biggestChallenge}</div>}
                                                    {formData.arcaPriorities && <div className="col-12"><strong>ARCA Priorities:</strong> {formData.arcaPriorities}</div>}
                                                    {formData.speakerInvite && <div className="col-sm-6"><strong>Speaker Suggestion:</strong> {formData.speakerInvite}</div>}
                                                    {formData.uniqueFeature && <div className="col-sm-6"><strong>Unique Feature:</strong> {formData.uniqueFeature}</div>}
                                                    {formData.additionalComments && <div className="col-12"><strong>Comments:</strong> {formData.additionalComments}</div>}
                                                    <div className="col-sm-6"><strong>Practice Area:</strong> {formData.primaryPracticeArea || <span className="text-muted">—</span>}</div>
                                                    <div className="col-12"><strong>Heard About ARCA:</strong> {formData.heardAboutArca.length ? formData.heardAboutArca.join(', ') : <span className="text-muted">—</span>}</div>
                                                    <div className="col-sm-4"><strong>Volunteer:</strong> {formData.interestedVolunteering || <span className="text-muted">—</span>}</div>
                                                    <div className="col-sm-4"><strong>Receive Updates:</strong> {formData.receiveUpdates || <span className="text-muted">—</span>}</div>
                                                    {formData.receiveUpdates === 'Yes' && formData.email && <div className="col-sm-4"><strong>Email:</strong> {formData.email}</div>}
                                                </div>
                                            </motion.div>
                                        </motion.div>
                                    )}

                                    {/* Navigation Buttons */}
                                    <div className="d-flex justify-content-between mt-5 pt-3" style={{ borderTop: `2px solid ${ARCA.gray100}` }}>
                                        {showReview ? (
                                            <>
                                                <motion.button
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                    className="btn px-4 py-2"
                                                    onClick={() => { setShowReview(false); setStep(3); }}
                                                    style={{ border: `1.5px solid ${ARCA.gray200}`, color: ARCA.gray600, borderRadius: 10, fontWeight: 500 }}
                                                >
                                                    <i className="bi bi-arrow-left me-2"></i>Back to Edit
                                                </motion.button>
                                                <motion.button
                                                    whileHover={{ scale: 1.03, boxShadow: `0 4px 16px ${ARCA.primaryBorder}` }}
                                                    whileTap={{ scale: 0.97 }}
                                                    className="btn px-5 py-2"
                                                    onClick={handleSubmit}
                                                    disabled={submitting}
                                                    style={{ backgroundColor: ARCA.primary, borderColor: ARCA.primary, color: ARCA.white, borderRadius: 10, fontWeight: 600, borderWidth: 1.5 }}
                                                >
                                                    {submitting ? (
                                                        <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Submitting...</>
                                                    ) : (
                                                        <><i className="bi bi-check-circle me-2"></i>Confirm & Submit</>
                                                    )}
                                                </motion.button>
                                            </>
                                        ) : (
                                            <>
                                                {step > 1 ? (
                                                    <motion.button
                                                        whileHover={{ scale: 1.02 }}
                                                        whileTap={{ scale: 0.98 }}
                                                        className="btn px-4 py-2"
                                                        onClick={prevStep}
                                                        style={{ border: `1.5px solid ${ARCA.gray200}`, color: ARCA.gray600, borderRadius: 10, fontWeight: 500 }}
                                                    >
                                                        <i className="bi bi-arrow-left me-2"></i>Previous
                                                    </motion.button>
                                                ) : <div />}

                                                {step < 3 ? (
                                                    <motion.button
                                                        whileHover={{ scale: 1.03, boxShadow: `0 4px 16px ${ARCA.primaryBorder}` }}
                                                        whileTap={{ scale: 0.97 }}
                                                        className="btn px-4 py-2"
                                                        onClick={nextStep}
                                                        style={{ backgroundColor: ARCA.primary, borderColor: ARCA.primary, color: ARCA.white, borderRadius: 10, fontWeight: 600, borderWidth: 1.5 }}
                                                    >
                                                        Next<i className="bi bi-arrow-right ms-2"></i>
                                                    </motion.button>
                                                ) : (
                                                    <motion.button
                                                        whileHover={{ scale: 1.03, boxShadow: `0 4px 16px ${ARCA.primaryBorder}` }}
                                                        whileTap={{ scale: 0.97 }}
                                                        className="btn px-5 py-2"
                                                        onClick={() => setShowReview(true)}
                                                        style={{ backgroundColor: ARCA.primary, borderColor: ARCA.primary, color: ARCA.white, borderRadius: 10, fontWeight: 600, borderWidth: 1.5 }}
                                                    >
                                                        <i className="bi bi-clipboard-check me-2"></i>Review & Submit
                                                    </motion.button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </PublicLayout>
    );
};

export default Arca2027SurveyPage;
